<script setup lang="ts">
import type { ISportAngle } from "~/videos/types/event-angles";
import { MenuItemVideoDownload, ModalsShareDownloadableFiles } from "#components";
import { type IBasicVideo, type IEvent, type TVideoSourceType, VIDEO_SOURCE_TYPE } from "@/types";
import { PLAY_BASED_GAMES, SHARED_DOWNLOADABLE_FILES_SOURCE_TYPE } from "~/constants";
import { useClipAnglesAvailability } from "~/videos/composables/use-clip-angles-availability";

const { t } = useI18n();
const modal = useModal();

const props = defineProps<{
  video: IEvent | IBasicVideo;
  availableClipAngles?: ISportAngle[];
  clipAnglesError?: unknown;
  clipAnglesLoading?: boolean;
}>();

const canShareDownloadableFiles = computed(() => (props.video as IEvent).permissions.canShareDownloadableFiles);
const isPlayBasedEvent = computed(() => PLAY_BASED_GAMES.includes((props.video as IEvent).sport_type));

function isAvailableType(type: TVideoSourceType): boolean {
  return Boolean((props.video?.angles?.[type]?.size ?? 0) > 0);
}

const availableTypes = computed(() => {
  return {
    [SHARED_DOWNLOADABLE_FILES_SOURCE_TYPE.CLIPPED]: isAvailableType(VIDEO_SOURCE_TYPE.CLIPPED) || Boolean(props.availableClipAngles?.length),
    [SHARED_DOWNLOADABLE_FILES_SOURCE_TYPE.FULL]: isAvailableType(VIDEO_SOURCE_TYPE.CONTINUOUS),
  };
});

const { isClipAnglesUnavailable, clipAnglesUnavailableTooltip } = useClipAnglesAvailability({
  availableClipAngles: computed(() => props.availableClipAngles),
  clipAnglesError: computed(() => props.clipAnglesError),
  clipAnglesLoading: computed(() => props.clipAnglesLoading),
});

function onClick(type: "download-full" | "download-clipped") {
  modal.open(ModalsShareDownloadableFiles, {
    modelValue: true,
    event: props.video as IEvent,
    type,
    availableClipAngles: props.availableClipAngles,
    clipAnglesLoading: props.clipAnglesLoading,
  });
}
</script>

<template>
  <template v-if="isPlayBasedEvent && canShareDownloadableFiles">
    <UListItem
      v-if="availableTypes[SHARED_DOWNLOADABLE_FILES_SOURCE_TYPE.FULL]"
      icon="download-media"
      :text="t('labels.download_full_video')"
      @click="onClick('download-full')"
    />
    <UListItem
      v-if="availableTypes[SHARED_DOWNLOADABLE_FILES_SOURCE_TYPE.CLIPPED]"
      icon="download-clipped"
      :text="t('labels.download_clipped_video')"
      :disabled="isClipAnglesUnavailable"
      :tooltip="isClipAnglesUnavailable ? clipAnglesUnavailableTooltip : undefined"
      :on-click="() => onClick('download-clipped')"
    />
  </template>
  <template v-else>
    <MenuItemVideoDownload
      v-for="[type] in props.video.angles?.continuous"
      :key="type"
      :type="type"
      :video="props.video"
    />
  </template>
</template>
